import React, { useLayoutEffect, useRef } from 'react';

const STAGE_NAMES = ['도입', '배움1', '배움2', '정리'] as const;
const STAR_TAGS = ['활동', '내용', '핵심질문', '평가질문', '확인기준', '피드백'] as const;
const HASH_TAGS = ['형태', '기능'] as const;
const ZWSP = '\u200B';

type Mark = '#' | '*';
type FlowChip = { marker: Mark; tag: string; text: string; closed: boolean };

type GuideLine =
  | { kind: 'stage'; label: string; text: string; chips: FlowChip[] }
  | { kind: 'item'; tag: string; text: string; marker: Mark; closed: boolean; chips: FlowChip[] }
  | { kind: 'step'; n: string; text: string }
  | { kind: 'label'; label: string; text: string }
  | { kind: 'text'; text: string };

type TextAtom = { type: 'text'; node: Text; raw: string };
type ChipAtom = { type: 'chip'; el: HTMLElement; kind: string; value: string; marker: string; closed: boolean };
type Atom = TextAtom | ChipAtom;
type Segment = { type: 'plain'; texts: TextAtom[] } | { type: 'chipped'; chip: ChipAtom; texts: TextAtom[] };

interface LiveRow {
  host: HTMLElement;
  loose: boolean;
  nodes: Node[];
}

function isStageName(value: string): boolean {
  return (STAGE_NAMES as readonly string[]).includes(value);
}

function isMetaTag(value: string): boolean {
  return (HASH_TAGS as readonly string[]).includes(value);
}

function canonicalMarker(tag: string, typed: Mark): Mark {
  if ((STAR_TAGS as readonly string[]).includes(tag)) return '*';
  if (isMetaTag(tag)) return '#';
  return typed;
}

function itemChipClass(marker: string): string {
  return marker === '#' ? 'cp-learning-plan-guide-item is-meta' : 'cp-learning-plan-guide-item';
}

function findMarker(input: string, from: number): number {
  for (let i = from; i < input.length; i += 1) {
    const ch = input[i];
    if (ch !== '*' && ch !== '#') continue;
    if (i > 0 && !/\s/.test(input[i - 1])) continue;
    let j = i + 1;
    if (j >= input.length || /\s/.test(input[j])) continue;
    while (j < input.length && !/\s/.test(input[j])) j += 1;
    if (j >= input.length || !/\s/.test(input[j])) continue;
    return i;
  }
  return -1;
}

function scanFlow(input: string): { lead: string; chips: FlowChip[] } {
  const first = findMarker(input, 0);
  if (first < 0) return { lead: input, chips: [] };
  const chips: FlowChip[] = [];
  let i = first;
  while (i < input.length && (input[i] === '*' || input[i] === '#')) {
    const typed: Mark = input[i] === '#' ? '#' : '*';
    i += 1;
    const tagStart = i;
    while (i < input.length && !/\s/.test(input[i])) i += 1;
    const tag = input.slice(tagStart, i);
    if (!tag || i >= input.length || !/\s/.test(input[i])) break;
    i += 1;
    const next = findMarker(input, i);
    const end = next < 0 ? input.length : next;
    const text = input.slice(i, end).replace(/[ \t]+$/g, '');
    i = next < 0 ? input.length : next;
    chips.push({ marker: canonicalMarker(tag, typed), tag, text, closed: true });
  }
  if (!chips.length) return { lead: input, chips: [] };
  return { lead: input.slice(0, first), chips };
}

function foldStageChips(lines: GuideLine[]): GuideLine[] {
  const consumed = new Set<number>();
  const out: GuideLine[] = [];
  for (let i = 0; i < lines.length; i += 1) {
    if (consumed.has(i)) continue;
    const line = lines[i];
    if (line.kind !== 'stage') {
      out.push(line);
      continue;
    }
    const chips = [...line.chips];
    for (let j = i + 1; j < lines.length; j += 1) {
      const next = lines[j];
      if (next.kind === 'stage') break;
      if (next.kind === 'item' && isMetaTag(next.tag)) {
        chips.push({ marker: next.marker, tag: next.tag, text: next.text, closed: next.closed });
        next.chips.forEach((extra) => chips.push(extra));
        consumed.add(j);
      }
    }
    out.push({ ...line, chips });
  }
  return out;
}

function isElement(node: Node | null): node is HTMLElement {
  return !!node && node.nodeType === 1;
}

function visibleOf(value: string): string {
  return value.replace(/\u200B/g, '');
}

export function parseLearningPlanGuide(guide: string): GuideLine[] {
  const lines = guide.split('\n');
  if (lines.length === 1 && lines[0] === '') return [];
  const parsed = lines.map((line): GuideLine => {
    const stage = line.match(/^(도입|배움1|배움2|정리):[ \t]?(.*)$/);
    if (stage) {
      const flow = scanFlow(stage[2] ?? '');
      const lead = flow.lead.replace(/[ \t]+$/g, '');
      return { kind: 'stage', label: stage[1], text: lead.trim() ? lead : '', chips: flow.chips };
    }
    const marked = markerLine(line);
    if (marked) return marked;
    const bracket = line.match(/^\[([^\]\n]{1,20})\]\s*(.*)$/);
    if (bracket) return { kind: 'label', label: bracket[1].trim(), text: bracket[2] };
    const step = line.match(/^(\d+)\.\s*(.*)$/);
    if (step) return { kind: 'step', n: step[1], text: step[2] };
    const label = line.match(/^([^\d:\n][^:\n]{0,19}):\s*(.*)$/);
    if (label) {
      const name = label[1].trim();
      if (name === '학습 목표' || isStageName(name) || /도입|배움|정리/.test(name)) {
        return { kind: 'text', text: line };
      }
      return { kind: 'label', label: name, text: label[2] };
    }
    return { kind: 'text', text: line };
  });
  return foldStageChips(parsed);
}

function markerLine(line: string): GuideLine | null {
  const flow = scanFlow(line.replace(/^\u200B+/, ''));
  if (!flow.chips.length || flow.lead.trim()) return null;
  const [first, ...rest] = flow.chips;
  return {
    kind: 'item',
    tag: first.tag,
    text: first.text,
    marker: first.marker,
    closed: first.closed,
    chips: rest,
  };
}

export function learningPlanGuideIsStructured(guide: string): boolean {
  return parseLearningPlanGuide(guide).some((line) => line.kind !== 'text');
}

export function learningPlanGuideHasPlanMarkers(guide: string): boolean {
  return parseLearningPlanGuide(guide).some((line) => line.kind === 'stage' || line.kind === 'item');
}

function serializeChip(chip: FlowChip): string {
  const head = `${chip.marker}${chip.tag}`;
  if (!chip.closed) return head;
  return chip.text ? `${head} ${chip.text}` : `${head} `;
}

function joinChips(chips: FlowChip[]): string {
  return chips.map((chip, index) => (index === 0 ? serializeChip(chip) : ` ${serializeChip(chip)}`)).join('');
}

function serializeLine(line: GuideLine): string {
  if (line.kind === 'stage') {
    const head = line.text ? `${line.label}: ${line.text}` : `${line.label}:`;
    if (!line.chips.length) return head;
    const chips = joinChips(line.chips);
    return line.text ? `${head} ${chips}` : `${line.label}: ${chips}`;
  }
  if (line.kind === 'item') {
    const main = serializeChip({
      marker: line.marker,
      tag: line.tag,
      text: line.text,
      closed: line.closed,
    });
    if (!line.chips.length) return main;
    return `${main}${main.endsWith(' ') ? '' : ' '}${joinChips(line.chips)}`;
  }
  if (line.kind === 'step') return line.text ? `${line.n}. ${line.text}` : `${line.n}.`;
  if (line.kind === 'label') return line.text ? `${line.label}: ${line.text}` : `${line.label}:`;
  return line.text;
}

export function serializeLearningPlanGuide(lines: GuideLine[]): string {
  return lines.map(serializeLine).join('\n');
}

function lineBody(line: GuideLine): string {
  return line.text;
}

function chipKey(chip: FlowChip): string {
  return `${chip.marker}${chip.tag}${chip.closed ? '.' : ''}`;
}

function structureKey(guide: string): string {
  return parseLearningPlanGuide(guide)
    .map((line) => {
      if (line.kind === 'stage') return `s:${line.label}:${line.chips.map(chipKey).join('+')}`;
      if (line.kind === 'item') {
        return `i:${chipKey({ marker: line.marker, tag: line.tag, text: '', closed: line.closed })}+${line.chips.map(chipKey).join('+')}`;
      }
      if (line.kind === 'step') return `n:${line.n}`;
      if (line.kind === 'label') return `l:${line.label}`;
      return 't';
    })
    .join('\n');
}

function markerText(kind: string, value: string, prefix = ''): string {
  if (kind === 'item') {
    const mark: Mark = prefix === '#' || prefix === '*' ? prefix : canonicalMarker(value, '#');
    return `${mark}${value}`;
  }
  if (kind === 'step') return `${value}.`;
  return `${value}:`;
}

function chipSource(kind: string, value: string, tail: string, prefix = '', closed = false): string {
  const marker = markerText(kind, value, prefix);
  if (kind === 'item') {
    if (!closed) return `${marker}${tail}`;
    if (!tail) return `${marker} `;
    if (!tail.trim()) return `${marker}${tail}`;
    return `${marker} ${tail}`;
  }
  if (kind === 'stage') {
    if (!tail) return marker;
    if (!tail.trim()) return tail.startsWith(' ') ? `${marker}${tail}` : `${marker} ${tail}`;
    return `${marker} ${tail}`;
  }
  return tail ? `${marker} ${tail}` : marker;
}

function gapBeforeTail(kind: string, closed: boolean, tail: string): number {
  if (kind === 'item') {
    if (!closed) return 0;
    if (!tail.trim()) return tail ? 0 : 1;
    return 1;
  }
  if (!tail || !tail.trim()) return 0;
  return 1;
}

function renderChip(doc: Document, line: Exclude<GuideLine, { kind: 'text' }>): HTMLElement {
  const span = doc.createElement('span');
  span.setAttribute('contenteditable', 'false');
  span.setAttribute('draggable', 'false');
  const text = doc.createElement('span');
  text.className = 'cp-learning-plan-guide-chip-text';
  if (line.kind === 'stage') {
    span.className = 'cp-learning-plan-guide-stage';
    span.dataset.chip = 'stage';
    span.dataset.value = line.label;
    text.textContent = line.label;
  } else if (line.kind === 'item') {
    span.className = itemChipClass(line.marker);
    span.dataset.chip = 'item';
    span.dataset.value = line.tag;
    span.dataset.marker = line.marker;
    if (line.closed) span.dataset.closed = '1';
    text.textContent = line.tag;
  } else if (line.kind === 'step') {
    span.className = 'cp-learning-plan-guide-num';
    span.dataset.chip = 'step';
    span.dataset.value = line.n;
    text.textContent = line.n;
  } else {
    span.className = 'cp-learning-plan-guide-label';
    span.dataset.chip = 'label';
    span.dataset.value = line.label;
    text.textContent = line.label;
  }
  span.appendChild(text);
  return span;
}

function appendTail(doc: Document, row: HTMLElement, text: string, padEnd: boolean): void {
  const tail = doc.createElement('span');
  tail.className = 'cp-lp-tail';
  if (padEnd) tail.textContent = text ? (text.endsWith(' ') ? text : `${text} `) : ' ';
  else tail.textContent = text || ZWSP;
  row.appendChild(tail);
}

function flowItem(chip: FlowChip): Extract<GuideLine, { kind: 'item' }> {
  return { kind: 'item', marker: chip.marker, tag: chip.tag, text: chip.text, closed: chip.closed, chips: [] };
}

function appendFlow(doc: Document, row: HTMLElement, text: string, chips: FlowChip[]): void {
  appendTail(doc, row, text, chips.length > 0);
  chips.forEach((chip, index) => {
    row.appendChild(renderChip(doc, flowItem(chip)));
    appendTail(doc, row, chip.text, index < chips.length - 1);
  });
}

function renderRow(doc: Document, line: GuideLine): HTMLElement {
  const row = doc.createElement('div');
  row.className = line.kind === 'text' ? 'cp-lp-row' : `cp-lp-row is-chip is-${line.kind}`;
  if (line.kind === 'text') {
    if (line.text) row.textContent = line.text;
    else row.appendChild(doc.createElement('br'));
    return row;
  }
  if (line.kind === 'stage') {
    row.appendChild(renderChip(doc, line));
    appendFlow(doc, row, line.text, line.chips);
    return row;
  }
  if (line.kind === 'item') {
    row.appendChild(renderChip(doc, line));
    appendFlow(doc, row, line.text, line.chips);
    return row;
  }
  row.appendChild(renderChip(doc, line));
  appendTail(doc, row, line.text, false);
  return row;
}

export function renderGuideInto(root: HTMLElement, guide: string): void {
  const doc = root.ownerDocument;
  const lines = parseLearningPlanGuide(guide);
  const rows = lines.length ? lines : [{ kind: 'text' as const, text: '' }];
  root.replaceChildren(...rows.map((line) => renderRow(doc, line)));
}

function walkAtoms(node: Node, atoms: Atom[]): void {
  if (node.nodeType === 3) {
    atoms.push({ type: 'text', node: node as Text, raw: node.textContent || '' });
    return;
  }
  if (!isElement(node)) return;
  if (node.dataset.chip) {
    atoms.push({
      type: 'chip',
      el: node,
      kind: node.dataset.chip,
      value: node.dataset.value || '',
      marker: node.dataset.marker || '',
      closed: node.dataset.closed === '1',
    });
    return;
  }
  if (node.tagName === 'BR') return;
  node.childNodes.forEach((child) => walkAtoms(child, atoms));
}

function atomsFromNodes(nodes: Node[]): Atom[] {
  const atoms: Atom[] = [];
  nodes.forEach((node) => walkAtoms(node, atoms));
  return atoms;
}

function segmentsOf(atoms: Atom[]): Segment[] {
  const segs: Segment[] = [];
  for (let i = 0; i < atoms.length; i += 1) {
    const atom = atoms[i];
    if (atom.type === 'chip') {
      const texts: TextAtom[] = [];
      while (i + 1 < atoms.length && atoms[i + 1].type === 'text') {
        i += 1;
        texts.push(atoms[i] as TextAtom);
      }
      segs.push({ type: 'chipped', chip: atom, texts });
    } else {
      const texts: TextAtom[] = [atom];
      while (i + 1 < atoms.length && atoms[i + 1].type === 'text') {
        i += 1;
        texts.push(atoms[i] as TextAtom);
      }
      segs.push({ type: 'plain', texts });
    }
  }
  return segs;
}

function textsVisible(texts: TextAtom[]): string {
  return texts.map((text) => visibleOf(text.raw)).join('');
}

function segmentSource(seg: Segment): string {
  if (seg.type === 'plain') return textsVisible(seg.texts);
  return chipSource(seg.chip.kind, seg.chip.value, textsVisible(seg.texts), seg.chip.marker, seg.chip.closed);
}

function liveRows(root: HTMLElement): LiveRow[] {
  const rows: LiveRow[] = [];
  let loose: Node[] = [];
  const flushLoose = () => {
    if (!loose.length) return;
    rows.push({ host: root, loose: true, nodes: loose });
    loose = [];
  };
  Array.from(root.childNodes).forEach((child) => {
    if (isElement(child) && (child.tagName === 'DIV' || child.tagName === 'P')) {
      flushLoose();
      rows.push({ host: child, loose: false, nodes: Array.from(child.childNodes) });
      return;
    }
    if (isElement(child) && child.tagName === 'BR') {
      flushLoose();
      rows.push({ host: root, loose: true, nodes: [] });
      return;
    }
    if (child.nodeType === 3 && !visibleOf(child.textContent || '') && loose.length === 0) return;
    loose.push(child);
  });
  flushLoose();
  return rows;
}

function rowSegments(row: LiveRow): Segment[] {
  return segmentsOf(atomsFromNodes(row.nodes));
}

function rowSource(row: LiveRow): string {
  return rowSegments(row).map(segmentSource).join('');
}

function rowHolds(row: LiveRow, target: Node): boolean {
  if (!row.loose && (row.host === target || row.host.contains(target))) return true;
  return row.nodes.some((node) => node === target || (isElement(node) && node.contains(target)));
}

function visibleOffset(raw: string, offset: number): number {
  let count = 0;
  const end = Math.max(0, Math.min(offset, raw.length));
  for (let i = 0; i < end; i += 1) if (raw[i] !== ZWSP) count += 1;
  return count;
}

function pointInTexts(texts: TextAtom[], target: Node, targetOffset: number): number | null {
  let seen = 0;
  for (const text of texts) {
    if (text.node === target) return seen + visibleOffset(text.raw, targetOffset);
    seen += visibleOf(text.raw).length;
  }
  for (const text of texts) {
    const parent = text.node.parentNode;
    if (parent !== target) continue;
    if (targetOffset <= 0) return 0;
    const children = Array.from(parent.childNodes);
    if (targetOffset >= children.length) return textsVisible(texts).length;
    let inner = 0;
    for (let i = 0; i < children.length; i += 1) {
      if (i === targetOffset) return inner;
      if (children[i].nodeType === 3) inner += visibleOf(children[i].textContent || '').length;
    }
    return inner;
  }
  return null;
}

function offsetInSegment(seg: Segment, target: Node, targetOffset: number): number | null {
  if (seg.type === 'plain') return pointInTexts(seg.texts, target, targetOffset);
  const marker = markerText(seg.chip.kind, seg.chip.value, seg.chip.marker);
  const tail = textsVisible(seg.texts);
  const sep = gapBeforeTail(seg.chip.kind, seg.chip.closed, tail);
  const chip = seg.chip.el;
  if (target === chip || chip.contains(target)) {
    if (target === chip && targetOffset === 0) return 0;
    if (target.nodeType === 3 && chip.contains(target)) {
      const labelAt = visibleOffset(target.textContent || '', targetOffset);
      if (seg.chip.kind === 'item') return Math.min(marker.length, 1 + labelAt);
      return Math.min(marker.length, labelAt);
    }
    return marker.length + sep;
  }
  const inTail = pointInTexts(seg.texts, target, targetOffset);
  if (inTail == null) return null;
  return marker.length + sep + inTail;
}

function sourceLengthOf(node: Node, previous: Node | null): number {
  if (isElement(node) && node.dataset.chip) {
    return markerText(node.dataset.chip, node.dataset.value || '', node.dataset.marker || '').length;
  }
  if (isElement(node) && node.querySelector('[data-chip]')) {
    let total = 0;
    let prev: Node | null = null;
    Array.from(node.childNodes).forEach((child) => {
      total += sourceLengthOf(child, prev);
      prev = child;
    });
    return total;
  }
  const text = visibleOf(node.textContent || '');
  if (isElement(previous) && previous.dataset.chip) {
    return gapBeforeTail(previous.dataset.chip, previous.dataset.closed === '1', text) + text.length;
  }
  return text.length;
}

function offsetBeforeChild(row: HTMLElement, child: Node): number {
  let pos = 0;
  let prev: Node | null = null;
  for (const current of Array.from(row.childNodes)) {
    if (current === child) {
      const text = visibleOf(current.textContent || '');
      if (isElement(prev) && prev.dataset.chip && !(isElement(current) && current.dataset.chip)) {
        return pos + gapBeforeTail(prev.dataset.chip, prev.dataset.closed === '1', text);
      }
      return pos;
    }
    pos += sourceLengthOf(current, prev);
    prev = current;
  }
  return pos;
}

function offsetInRow(row: LiveRow, target: Node, targetOffset: number): number {
  if (!row.loose && target === row.host) {
    if (targetOffset <= 0) return 0;
    if (targetOffset >= row.host.childNodes.length) return rowSource(row).length;
    return offsetBeforeChild(row.host, row.host.childNodes[targetOffset]);
  }
  let pos = 0;
  for (const seg of rowSegments(row)) {
    const hit = offsetInSegment(seg, target, targetOffset);
    if (hit != null) return pos + hit;
    pos += segmentSource(seg).length;
  }
  return pos;
}

export function domPointToSource(root: HTMLElement, target: Node, targetOffset: number): number {
  const rows = liveRows(root);
  if (target === root) {
    let source = 0;
    const index = Math.max(0, Math.min(targetOffset, root.childNodes.length));
    let consumed = 0;
    for (let i = 0; i < rows.length; i += 1) {
      if (i > 0) source += 1;
      const row = rows[i];
      const span = row.loose ? row.nodes.length || 1 : 1;
      if (consumed >= index) return source;
      consumed += span;
      if (consumed > index && !row.loose) return source;
      source += rowSource(row).length;
    }
    return source;
  }
  let source = 0;
  for (let i = 0; i < rows.length; i += 1) {
    if (i > 0) source += 1;
    if (rowHolds(rows[i], target)) return source + offsetInRow(rows[i], target, targetOffset);
    source += rowSource(rows[i]).length;
  }
  return source;
}

function caretInTexts(texts: TextAtom[], visibleIndex: number): { node: Node; offset: number } | null {
  if (!texts.length) return null;
  let left = Math.max(0, visibleIndex);
  for (const text of texts) {
    let seen = 0;
    for (let i = 0; i <= text.raw.length; i += 1) {
      if (seen === left) return { node: text.node, offset: i };
      if (i === text.raw.length) break;
      if (text.raw[i] !== ZWSP) seen += 1;
    }
  }
  const last = texts[texts.length - 1];
  return { node: last.node, offset: last.raw.length };
}

function caretInRow(row: LiveRow, offset: number): { node: Node; offset: number } {
  const segs = rowSegments(row);
  let pos = 0;
  for (const seg of segs) {
    const len = segmentSource(seg).length;
    if (offset <= pos + len) {
      const local = offset - pos;
      if (seg.type === 'plain') {
        return caretInTexts(seg.texts, local) || { node: row.host, offset: row.host.childNodes.length };
      }
      const marker = markerText(seg.chip.kind, seg.chip.value, seg.chip.marker);
      const tail = textsVisible(seg.texts);
      const sep = gapBeforeTail(seg.chip.kind, seg.chip.closed, tail);
      if (local <= 0) {
        const index = Array.from(row.host.childNodes).indexOf(seg.chip.el);
        return { node: row.host, offset: Math.max(0, index) };
      }
      const intoTail = local <= marker.length + sep ? 0 : local - marker.length - sep;
      return caretInTexts(seg.texts, intoTail) || { node: row.host, offset: row.host.childNodes.length };
    }
    pos += len;
  }
  if (!row.loose && row.host.lastChild) return { node: row.host, offset: row.host.childNodes.length };
  return { node: row.host, offset: 0 };
}

export function resolveGuideCaret(root: HTMLElement, sourceOffset: number): { node: Node; offset: number } {
  const rows = liveRows(root);
  if (!rows.length) return { node: root, offset: 0 };
  let remaining = Math.max(0, sourceOffset);
  for (let i = 0; i < rows.length; i += 1) {
    if (i > 0) {
      if (remaining === 0) return caretInRow(rows[i], 0);
      remaining -= 1;
    }
    const len = rowSource(rows[i]).length;
    if (remaining <= len || i === rows.length - 1) return caretInRow(rows[i], Math.min(remaining, len));
    remaining -= len;
  }
  return { node: root, offset: root.childNodes.length };
}

export function serializeEditableRoot(root: HTMLElement): string {
  return liveRows(root)
    .map((row) => rowSource(row))
    .join('\n');
}

function selectionOf(root: HTMLElement): Selection | null {
  const doc = root.ownerDocument;
  const view = doc.defaultView;
  return doc.getSelection?.() || view?.getSelection?.() || null;
}

export function placeGuideCaret(root: HTMLElement, sourceOffset: number): void {
  const point = resolveGuideCaret(root, sourceOffset);
  const doc = root.ownerDocument;
  const sel = selectionOf(root);
  if (!sel || !doc.createRange) return;
  const range = doc.createRange();
  if (typeof range.setStart !== 'function' || typeof sel.removeAllRanges !== 'function') return;
  const max = point.node.nodeType === 3 ? (point.node.textContent || '').length : point.node.childNodes.length;
  range.setStart(point.node, Math.max(0, Math.min(point.offset, max)));
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
}

function readCaret(root: HTMLElement, fallback: number): number {
  const sel = selectionOf(root);
  if (!sel || sel.rangeCount === 0 || !sel.anchorNode || !root.contains(sel.anchorNode)) return fallback;
  return domPointToSource(root, sel.anchorNode, sel.anchorOffset);
}

function domIsCanonical(root: HTMLElement): boolean {
  return Array.from(root.childNodes).every((child) => {
    if (!isElement(child) || child.tagName !== 'DIV' || !child.classList.contains('cp-lp-row')) return false;
    return Array.from(child.childNodes).every((grand) => {
      if (grand.nodeType === 3) return true;
      if (!isElement(grand)) return false;
      if (grand.tagName === 'BR') return true;
      if (grand.dataset.chip) return true;
      if (!grand.classList.contains('cp-lp-tail')) return false;
      return Array.from(grand.childNodes).every((inner) => inner.nodeType === 3);
    });
  });
}

function chipFromTarget(target: Node | null): HTMLElement | null {
  let current: Node | null = target;
  while (current) {
    if (isElement(current) && current.dataset.chip) return current;
    current = current.parentNode;
  }
  return null;
}

export function guideChipBeside(node: Node, offset: number, dir: 'before' | 'after'): HTMLElement | null {
  if (node.nodeType === 3) {
    const raw = node.textContent || '';
    const slice = dir === 'before' ? raw.slice(0, offset) : raw.slice(offset);
    if (visibleOf(slice).length > 0) return null;
    return adjacentChip(node, dir);
  }
  if (!isElement(node)) return null;
  if (node.dataset.chip) {
    if (dir === 'before' && offset > 0) return node;
    if (dir === 'after' && offset === 0) return node;
    return null;
  }
  const child = dir === 'before' ? node.childNodes[offset - 1] : node.childNodes[offset];
  if (child) {
    if (isElement(child) && child.dataset.chip) return child;
    if (child.nodeType === 3 && !visibleOf(child.textContent || '')) return adjacentChip(child, dir);
    if (isElement(child) && child.classList.contains('cp-lp-tail') && !visibleOf(child.textContent || '')) {
      return dir === 'before' ? adjacentChip(child, 'before') : null;
    }
    return null;
  }
  if (node.classList.contains('cp-lp-tail')) return adjacentChip(node, dir);
  return null;
}

function adjacentChip(node: Node, dir: 'before' | 'after'): HTMLElement | null {
  let current: Node | null = dir === 'before' ? node.previousSibling : node.nextSibling;
  while (current) {
    if (current.nodeType === 3) {
      if (!visibleOf(current.textContent || '')) {
        current = dir === 'before' ? current.previousSibling : current.nextSibling;
        continue;
      }
      return null;
    }
    if (isElement(current) && current.dataset.chip) return current;
    if (isElement(current) && current.tagName === 'BR') {
      current = dir === 'before' ? current.previousSibling : current.nextSibling;
      continue;
    }
    if (isElement(current) && current.classList.contains('cp-lp-tail')) {
      if (dir === 'before' && !visibleOf(current.textContent || '')) {
        current = current.previousSibling;
        continue;
      }
      return null;
    }
    return null;
  }
  const parent = node.parentElement;
  if (parent && parent.classList.contains('cp-lp-tail')) return adjacentChip(parent, dir);
  return null;
}

function firstTailText(chip: HTMLElement): Text | null {
  const tail = chip.nextElementSibling;
  if (!tail || !tail.classList.contains('cp-lp-tail')) return null;
  const text = Array.from(tail.childNodes).find((node) => node.nodeType === 3);
  return (text as Text) || null;
}

export function mountGuideEditor(el: HTMLElement, onChange: (guide: string, caret: number) => void): {
  setExternalGuide: (guide: string, caret: number | null) => void;
  destroy: () => void;
} {
  let lastGuide: string | null = null;
  let applying = false;
  let composing = false;
  let pressedChip: HTMLElement | null = null;

  const emit = (next: string, caret: number, rebuild: boolean) => {
    lastGuide = next;
    if (rebuild) {
      applying = true;
      try {
        renderGuideInto(el, next);
        placeGuideCaret(el, caret);
      } finally {
        applying = false;
      }
    }
    onChange(next, caret);
  };

  const syncFromDom = () => {
    if (applying || composing) return;
    const next = serializeEditableRoot(el);
    if (next === lastGuide) return;
    const caret = readCaret(el, next.length);
    emit(next, caret, structureKey(next) !== structureKey(lastGuide || '') || !domIsCanonical(el));
  };

  const sourceNow = () => (lastGuide == null ? serializeEditableRoot(el) : lastGuide);

  const replaceSource = (insert: string) => {
    const source = serializeEditableRoot(el);
    const sel = selectionOf(el);
    if (!sel || sel.rangeCount === 0 || !sel.anchorNode || !el.contains(sel.anchorNode)) return;
    const startPoint = domPointToSource(el, sel.anchorNode, sel.anchorOffset);
    const endPoint =
      sel.focusNode && el.contains(sel.focusNode) ? domPointToSource(el, sel.focusNode, sel.focusOffset) : startPoint;
    const start = Math.min(startPoint, endPoint);
    const end = Math.max(startPoint, endPoint);
    const next = source.slice(0, start) + insert + source.slice(end);
    emit(next, start + insert.length, true);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.isComposing || composing) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      replaceSource('\n');
      return;
    }
    if ((event.metaKey || event.ctrlKey) && (event.key === 'b' || event.key === 'i' || event.key === 'u')) {
      event.preventDefault();
      return;
    }
    if (event.key !== 'Backspace' && event.key !== 'Delete') return;
    const sel = selectionOf(el);
    if (!sel || !sel.isCollapsed || !sel.anchorNode || !el.contains(sel.anchorNode)) return;
    const dir = event.key === 'Backspace' ? 'before' : 'after';
    const chip = guideChipBeside(sel.anchorNode, sel.anchorOffset, dir);
    if (chip && el.contains(chip)) {
      event.preventDefault();
      const caret = domPointToSource(el, chip, 0);
      if (event.key === 'Backspace' && chip.dataset.chip === 'item') {
        const label = chip.dataset.value || '';
        const marker = chip.dataset.marker === '#' ? '#' : '*';
        const source = serializeEditableRoot(el);
        const chars = Array.from(label);
        chars.pop();
        const nextLabel = chars.join('');
        const boundary = caret + marker.length + Array.from(label).length;
        let rest = source.slice(boundary);
        const completing = rest.startsWith(' ') || rest.startsWith('\t');
        if (completing) rest = rest.slice(1);
        const inserted = `${marker}${nextLabel}${rest ? ` ${rest}` : ''}`;
        const nextCaret = caret + marker.length + Array.from(nextLabel).length;
        emit(source.slice(0, caret) + inserted, nextCaret, true);
        return;
      }
      chip.remove();
      emit(serializeEditableRoot(el), caret, true);
      return;
    }
    const source = sourceNow();
    const caret = readCaret(el, source.length);
    if (event.key === 'Backspace' && caret > 0 && source[caret - 1] === '\n') {
      event.preventDefault();
      emit(source.slice(0, caret - 1) + source.slice(caret), caret - 1, true);
      return;
    }
    if (event.key === 'Delete' && caret < source.length && source[caret] === '\n') {
      event.preventDefault();
      emit(source.slice(0, caret) + source.slice(caret + 1), caret, true);
    }
  };

  const onPaste = (event: ClipboardEvent) => {
    if (applying || composing) return;
    const text = event.clipboardData?.getData('text/plain');
    if (text == null) return;
    event.preventDefault();
    replaceSource(text.replace(/\r\n/g, '\n').replace(/\r/g, '\n'));
  };

  const onCopy = (event: ClipboardEvent) => {
    const sel = selectionOf(el);
    if (!sel || sel.isCollapsed || sel.rangeCount === 0 || !sel.anchorNode || !sel.focusNode) return;
    if (!el.contains(sel.anchorNode) || !el.contains(sel.focusNode) || !event.clipboardData) return;
    const source = serializeEditableRoot(el);
    const start = domPointToSource(el, sel.anchorNode, sel.anchorOffset);
    const end = domPointToSource(el, sel.focusNode, sel.focusOffset);
    event.preventDefault();
    event.clipboardData.setData('text/plain', source.slice(Math.min(start, end), Math.max(start, end)));
  };

  const onCut = (event: ClipboardEvent) => {
    onCopy(event);
    if (!event.defaultPrevented) return;
    replaceSource('');
  };

  const onInput = () => syncFromDom();
  const onCompositionStart = () => {
    composing = true;
  };
  const onCompositionEnd = () => {
    composing = false;
    syncFromDom();
  };
  const onMouseDown = (event: MouseEvent) => {
    pressedChip = chipFromTarget(event.target as Node | null);
  };
  const placeCaretAfterChip = (chip: HTMLElement) => {
    const text = firstTailText(chip);
    const sel = selectionOf(el);
    if (!text || !sel) return;
    const range = el.ownerDocument.createRange();
    range.setStart(text, 0);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  };

  const onMouseUp = () => {
    const chip = pressedChip;
    pressedChip = null;
    if (!chip || !el.contains(chip)) return;
    const sel = selectionOf(el);
    if (!sel || !sel.isCollapsed || !sel.anchorNode || !chip.contains(sel.anchorNode)) return;
    placeCaretAfterChip(chip);
  };

  el.addEventListener('keydown', onKeyDown);
  el.addEventListener('paste', onPaste);
  el.addEventListener('copy', onCopy);
  el.addEventListener('cut', onCut);
  el.addEventListener('input', onInput);
  el.addEventListener('compositionstart', onCompositionStart);
  el.addEventListener('compositionend', onCompositionEnd);
  el.addEventListener('mousedown', onMouseDown);
  el.addEventListener('mouseup', onMouseUp);

  return {
    setExternalGuide(guide: string, caret: number | null) {
      if (guide === lastGuide && caret == null) return;
      lastGuide = guide;
      applying = true;
      try {
        renderGuideInto(el, guide);
        if (caret != null) {
          el.focus();
          placeGuideCaret(el, caret);
        }
      } finally {
        applying = false;
      }
    },
    destroy() {
      el.removeEventListener('keydown', onKeyDown);
      el.removeEventListener('paste', onPaste);
      el.removeEventListener('copy', onCopy);
      el.removeEventListener('cut', onCut);
      el.removeEventListener('input', onInput);
      el.removeEventListener('compositionstart', onCompositionStart);
      el.removeEventListener('compositionend', onCompositionEnd);
      el.removeEventListener('mousedown', onMouseDown);
      el.removeEventListener('mouseup', onMouseUp);
    },
  };
}

export function LearningPlanGuideView({
  guide,
  editable,
  onChange,
  className,
  ariaLabel = '활동 설명',
}: {
  guide: string;
  editable?: boolean;
  onChange?: (guide: string) => void;
  className?: string;
  ariaLabel?: string;
}) {
  const lines = parseLearningPlanGuide(guide);
  const structured = lines.some((line) => line.kind !== 'text');
  const pendingCaret = useRef<number | null>(null);
  const plainRef = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    if (structured || !editable) return;
    const offset = pendingCaret.current;
    if (offset == null) return;
    pendingCaret.current = null;
    const node = plainRef.current;
    if (!node) return;
    node.focus();
    const next = Math.max(0, Math.min(offset, node.value.length));
    node.setSelectionRange(next, next);
  }, [structured, editable, guide]);

  if (!structured) {
    if (!editable) return <>{guide.trim() || ariaLabel}</>;
    return (
      <textarea
        ref={plainRef}
        className="cp-daily-note-editor-textarea cp-learning-plan-edit-guide"
        value={guide}
        placeholder={ariaLabel}
        aria-label={ariaLabel}
        onChange={(event) => {
          const value = event.target.value;
          if (learningPlanGuideIsStructured(value)) pendingCaret.current = event.target.selectionStart ?? value.length;
          onChange?.(value);
        }}
      />
    );
  }

  if (!editable) {
    return (
      <div
        className={className ? `cp-learning-plan-guide ${className}` : 'cp-learning-plan-guide'}
        aria-label={ariaLabel}
        onCopy={(event) => {
          const selection = window.getSelection();
          if (!selection || selection.isCollapsed) return;
          const root = event.currentTarget;
          if (!root.contains(selection.anchorNode) || !root.contains(selection.focusNode)) return;
          event.preventDefault();
          event.clipboardData.setData('text/plain', serializeLearningPlanGuide(lines));
        }}
      >
        {lines.map((line, index) => {
          if (line.kind === 'text' && !line.text.trim()) {
            return <div key={`gap-${index}`} className="cp-learning-plan-guide-gap" />;
          }
          const showBody =
            line.kind === 'text' ||
            line.kind === 'step' ||
            ((line.kind === 'item' || line.kind === 'stage' || line.kind === 'label') && line.text.trim().length > 0);
          return (
            <div key={`${line.kind}-${index}`} className={`cp-learning-plan-guide-line is-${line.kind}`}>
              {line.kind === 'step' ? (
                <span className="cp-learning-plan-guide-num">
                  <span className="cp-learning-plan-guide-chip-text">{line.n}</span>
                </span>
              ) : null}
              {line.kind === 'stage' ? (
                <span className="cp-learning-plan-guide-stage" aria-label={`${line.label} 단계`}>
                  <span className="cp-learning-plan-guide-chip-text">{line.label}</span>
                </span>
              ) : null}
              {line.kind === 'item' ? (
                <span className={itemChipClass(line.marker)}>
                  <span className="cp-learning-plan-guide-chip-text">{line.tag}</span>
                </span>
              ) : null}
              {line.kind === 'label' ? (
                <span className="cp-learning-plan-guide-label">
                  <span className="cp-learning-plan-guide-chip-text">{line.label}</span>
                </span>
              ) : null}
              {showBody ? <span className="cp-learning-plan-guide-text">{lineBody(line)}</span> : null}
              {line.kind === 'stage' || line.kind === 'item'
                ? line.chips.map((chip, chipIndex) => (
                    <span key={`${chip.marker}${chip.tag}-${chipIndex}`} className="cp-learning-plan-guide-meta-pair">
                      <span className={itemChipClass(chip.marker)}>
                        <span className="cp-learning-plan-guide-chip-text">{chip.tag}</span>
                      </span>
                      {chip.text.trim() ? (
                        <span className="cp-learning-plan-guide-text">{chip.text}</span>
                      ) : null}
                    </span>
                  ))
                : null}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <GuideEditor
      guide={guide}
      className={className}
      ariaLabel={ariaLabel}
      pendingCaret={pendingCaret}
      onChange={onChange}
    />
  );
}

function GuideEditor({
  guide,
  className,
  ariaLabel,
  pendingCaret,
  onChange,
}: {
  guide: string;
  className?: string;
  ariaLabel: string;
  pendingCaret: React.MutableRefObject<number | null>;
  onChange?: (guide: string) => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const onChangeRef = useRef(onChange);
  const guideRef = useRef(guide);
  const apiRef = useRef<ReturnType<typeof mountGuideEditor> | null>(null);
  onChangeRef.current = onChange;
  guideRef.current = guide;

  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const api = mountGuideEditor(el, (value, caret) => {
      if (!learningPlanGuideIsStructured(value)) pendingCaret.current = caret;
      onChangeRef.current?.(value);
    });
    apiRef.current = api;
    const caret = pendingCaret.current;
    pendingCaret.current = null;
    api.setExternalGuide(guideRef.current, caret);
    return () => {
      api.destroy();
      apiRef.current = null;
    };
  }, [pendingCaret]);

  useLayoutEffect(() => {
    apiRef.current?.setExternalGuide(guide, null);
  }, [guide]);

  return (
    <div
      ref={rootRef}
      className={['cp-learning-plan-guide', 'is-editing', className].filter(Boolean).join(' ')}
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-multiline="true"
      aria-label={ariaLabel}
      spellCheck={false}
    />
  );
}
